import React, { useRef } from 'react';
import { Typography } from 'housecall-ui';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';

import { Photo } from '../../state/types';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';
import { useToastController } from '../../toast';
import { Nullable } from '../../types/types';

import { IMAGE_ACCEPTED_TYPES, IMAGE_LIMIT, IMAGE_MAX_SIZE, IMAGE_TYPE_INPUT } from './consts';
import { checkImageDimensions } from './utils';

export const ERROR_SIZE_AND_DUPLICATE = 'Photos not uploaded due to the 10MB size limit or duplicates';
export const ERROR_SIZE = "Some photos exceeded the 10MB limit and weren't uploaded.";
export const ERROR_DUPLICATE = "Some photos weren't uploaded; they were duplicates.";
export const ERROR_DIMENSIONS = "Some photos weren't uploaded; they exceed 8000x8000px dimensions.";

const filterOutFiles = async (
  files: Nullable<FileList>
): Promise<{ files: File[]; exceededSize: boolean; exceededDimensions: boolean }> => {
  let exceededSize = false;
  let exceededDimensions = false;

  if (!files || files.length === 0) {
    return { files: [], exceededSize, exceededDimensions };
  }

  const fileArray = Array.from(files);
  const results = await Promise.all(
    fileArray.map(async (file) => {
      const isValidSize = IMAGE_ACCEPTED_TYPES.includes(file?.type) && file?.size <= IMAGE_MAX_SIZE;
      let isValidDimension = true;
      if (isValidSize) {
        isValidDimension = await checkImageDimensions(file);
      }
      return { file, isValidSize, isValidDimension };
    })
  );

  exceededSize = results.some((r) => !r.isValidSize);
  exceededDimensions = results.some((r) => r.isValidSize && !r.isValidDimension);

  return {
    files: results.filter((r) => r.isValidSize && r.isValidDimension).map((r) => r.file),
    exceededSize,
    exceededDimensions,
  };
};

const removeDuplicated = (files: File[], addedPhotos: Photo[]): { files: File[]; hasDuplicates: boolean } => {
  const names = files.map((file) => file.name);
  const addedPhotosNames = addedPhotos.map(({ file }) => file.name);

  let hasDuplicates = false;
  const uniqueNames = [...new Set(names)];
  uniqueNames.map((name) => files.find((file) => file.name === name));

  hasDuplicates = uniqueNames.length !== names.length;

  if (uniqueNames.some((name) => addedPhotosNames.includes(name))) {
    hasDuplicates = true;
  }

  return {
    files: uniqueNames
      .filter((name) => !addedPhotosNames.includes(name))
      .map((name) => files.find((file) => file.name === name)) as File[],
    hasDuplicates,
  };
};

type Props = {
  onChange: (photos: File[]) => void;
  photos: Photo[];
  label?: string;
  description?: string;
};

const generateToastCopy = (exceededSize: boolean, hasDuplicates: boolean, exceededDimensions: boolean) => {
  if (exceededSize && hasDuplicates) {
    return ERROR_SIZE_AND_DUPLICATE;
  }
  if (exceededSize) {
    return ERROR_SIZE;
  }
  if (hasDuplicates) {
    return ERROR_DUPLICATE;
  }
  if (exceededDimensions) {
    return ERROR_DIMENSIONS;
  }
};

const DEFAULT_DESCRIPTION = 'Add pictures to make your description more detailed.';
const DEFAULT_LABEL = 'Upload photos';

const UploadPhotosInput = ({
  onChange,
  photos: addedPhotos,
  label = DEFAULT_LABEL,
  description = DEFAULT_DESCRIPTION,
}: Props) => {
  const { state } = useOnlineBookingReducer();
  const inputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToastController();
  const { cart } = state;
  const photosCount = cart.reduce((prev, curr) => prev + (curr?.photos?.length || 0), 0);

  const handleAddAttachments = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const addedFiles = event.target.files;
    const { files: photos, exceededSize, exceededDimensions } = await filterOutFiles(addedFiles);

    const { files, hasDuplicates } = removeDuplicated(photos, addedPhotos);
    const toastMessage = generateToastCopy(exceededSize, hasDuplicates, exceededDimensions);
    if (toastMessage) {
      showToast(toastMessage);
    }

    const photosLimited: File[] = files.slice(0, IMAGE_LIMIT - photosCount);

    if (photosLimited) {
      onChange(photosLimited);
    }

    if (inputRef?.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <>
      <Typography variant="body1" gutterBottom>
        {description}
      </Typography>
      <Button
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        component="label"
        version="v2"
        color="secondary"
        variant="outlined"
      >
        {label}
        <input
          ref={inputRef}
          type="file"
          accept={IMAGE_TYPE_INPUT}
          multiple
          data-testid="attachment-upload"
          hidden
          onChange={handleAddAttachments}
        />
      </Button>
    </>
  );
};

export default UploadPhotosInput;
